// DashboardView - Vista principal del dashboard de engagement
// Incluye: KPIs, Tensión Competitiva (con precio de salida), Top Activos, Fricción, Perfil Financiero

import React, { useState, useMemo } from 'react';
import {
  Users,
  Eye,
  Target,
  Trophy,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  UserX,
  Clock,
  XCircle,
  CreditCard,
  Building,
  Home,
  Activity,
  DollarSign
} from 'lucide-react';
import { ActivityHeatmapModal } from './ActivityHeatmapModal';

/**
 * Dashboard principal de Engagement
 */
export const DashboardView = ({ buyerSummaries, property, isSpectatorMode = false }) => {
  const [showHeatmapModal, setShowHeatmapModal] = useState(false);

  // ============================================
  // MÉTRICAS KPI
  // ============================================
  const kpiMetrics = useMemo(() => {
    const invited = buyerSummaries.length;
    const visited = buyerSummaries.filter(b => b.visits > 0).length;
    const attempted = buyerSummaries.filter(b => b.conversionStatus === 'attempted' || b.hasBid).length;
    const bidded = buyerSummaries.filter(b => b.hasBid).length;

    return {
      invited,
      visited,
      attempted,
      bidded,
      visitRate: invited > 0 ? Math.round((visited / invited) * 100) : 0,
      attemptRate: visited > 0 ? Math.round((attempted / visited) * 100) : 0,
      conversionRate: attempted > 0 ? Math.round((bidded / attempted) * 100) : 0,
    };
  }, [buyerSummaries]);

  // ============================================
  // TENSIÓN COMPETITIVA (Top 3 pujas)
  // ============================================
  const competitiveTension = useMemo(() => {
    // Usar precio original si existe, sino el precio actual (para comparativas históricas)
    const startPrice = property?.originalStartPrice || property?.startPrice || property?.price || 0;

    // Obtener usuarios con pujas y su puja máxima
    const biddersWithMax = buyerSummaries
      .filter(b => b.hasBid && b.maxBidAmount > 0)
      .map(b => ({
        name: b.name,
        email: b.email,
        maxBid: b.maxBidAmount,
        maxBidTime: b.maxBidTime,
        bidsCount: b.bidsCount || 1,
      }))
      .sort((a, b) => b.maxBid - a.maxBid || new Date(a.maxBidTime) - new Date(b.maxBidTime))
      .slice(0, 3);

    // Calcular % vs precio de salida
    const withPercentage = biddersWithMax.map(b => ({
      ...b,
      percentVsStart: startPrice > 0 ? ((b.maxBid - startPrice) / startPrice * 100) : 0,
    }));

    // Stats generales
    const totalBidders = buyerSummaries.filter(b => b.hasBid).length;
    const attemptedWithoutBid = buyerSummaries.filter(
      b => b.conversionStatus === 'attempted' && !b.hasBid
    ).length;

    return {
      startPrice,
      topBidders: withPercentage,
      totalBidders,
      attemptedWithoutBid,
    };
  }, [buyerSummaries, property]);

  // ============================================
  // TOP ACTIVOS SIN PUJAR
  // ============================================
  const topActivosNoPujaron = useMemo(() => {
    return buyerSummaries
      .filter(b => {
        if (b.hasBid) return false;
        const score = b.interestScore ?? b.score ?? 0;
        const hasAttempted = b.conversionStatus === 'attempted';
        // Score >= 60, o score >= 40 si intentó
        return score >= 60 || (hasAttempted && score >= 40);
      })
      .sort((a, b) => (b.interestScore ?? b.score ?? 0) - (a.interestScore ?? a.score ?? 0))
      .slice(0, 3);
  }, [buyerSummaries]);

  // ============================================
  // FRICCIÓN
  // ============================================
  const frictionMetrics = useMemo(() => {
    const noVisits = buyerSummaries.filter(b => b.visits === 0).length;
    const singleVisit = buyerSummaries.filter(b => b.visits === 1 && !b.hasBid).length;
    const attemptedNotBid = buyerSummaries.filter(
      b => b.conversionStatus === 'attempted' && !b.hasBid
    ).length;
    const abandoned = buyerSummaries.filter(b => b.hasSubmitAttempt && !b.hasBid).length;

    return { noVisits, singleVisit, attemptedNotBid, abandoned };
  }, [buyerSummaries]);

  // ============================================
  // PERFIL FINANCIERO
  // ============================================
  const financialProfile = useMemo(() => {
    const withProfile = buyerSummaries.filter(b => b.profile);
    const total = withProfile.length;

    if (total === 0) return null;

    // Método de pago
    const paymentMethod = {
      cash: withProfile.filter(b => b.profile.paymentMethod === 'cash').length,
      finance_under_60: withProfile.filter(b => b.profile.paymentMethod === 'finance_under_60').length,
      finance_60_80: withProfile.filter(b => b.profile.paymentMethod === 'finance_60_80').length,
      finance_over_80: withProfile.filter(b => b.profile.paymentMethod === 'finance_over_80').length,
      undecided: withProfile.filter(b => b.profile.paymentMethod === 'undecided').length,
    };

    // Estado de financiación
    const financingStatus = {
      preapproved: withProfile.filter(b => b.profile.financingStatus === 'preapproved').length,
      in_process: withProfile.filter(b => b.profile.financingStatus === 'in_process').length,
      not_started: withProfile.filter(b => b.profile.financingStatus === 'not_started').length,
      no_need: withProfile.filter(b => b.profile.financingStatus === 'no_need').length,
    };

    // Propiedad que vender
    const propertyToSell = {
      no: withProfile.filter(b => b.profile.propertyToSell === 'no').length,
      signed_deposit: withProfile.filter(b => b.profile.propertyToSell === 'signed_deposit').length,
      on_sale: withProfile.filter(b => b.profile.propertyToSell === 'on_sale').length,
      not_yet_listed: withProfile.filter(b => b.profile.propertyToSell === 'not_yet_listed').length,
    };

    return { total, paymentMethod, financingStatus, propertyToSell };
  }, [buyerSummaries]);

  // ============================================
  // HELPERS
  // ============================================
  const formatAmount = (amount) => {
    if (!amount) return '0€';
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(2)}M€`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}k€`;
    return `${amount.toLocaleString()}€`;
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="space-y-6">
      {/* KPI CARDS - Ancho completo, altura moderada */}
      <div className="grid grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Invitados" value={kpiMetrics.invited} color="blue" />
        <KpiCard icon={Eye} label="Visitaron" value={kpiMetrics.visited} subtext={`${kpiMetrics.visitRate}%`} color="cyan" />
        <KpiCard icon={Target} label="Intentaron" value={kpiMetrics.attempted} subtext={`${kpiMetrics.attemptRate}%`} color="orange" />
        <KpiCard icon={Trophy} label="Pujaron" value={kpiMetrics.bidded} subtext={`${kpiMetrics.conversionRate}%`} color="green" />
      </div>

      {/* TENSIÓN COMPETITIVA */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-400" />
          Tensión Competitiva
        </h3>

        {competitiveTension.topBidders.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Aún no hay pujas registradas
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-3">
            {/* 1. PRECIO DE SALIDA */}
            <div className="bg-slate-700/50 rounded-xl border border-slate-600 p-4 flex flex-col justify-center text-center">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Precio salida</div>
              <div className="text-2xl font-bold text-white mb-3">{formatAmount(competitiveTension.startPrice)}</div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between text-slate-400">
                  <span>Mín aceptado:</span>
                  <span className="text-slate-300 font-medium">
                    {property?.minAbsolute ? formatAmount(property.minAbsolute) : '—'}
                  </span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Incremento:</span>
                  <span className="text-slate-300 font-medium">
                    {property?.minIncrement ? `${property.minIncrement.toLocaleString()}€` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* 2, 3, 4. RANKING: 1º, 2º, 3º */}
            {[0, 1, 2].map(idx => {
              const bidder = competitiveTension.topBidders[idx];
              const leaderBid = competitiveTension.topBidders[0]?.maxBid || 0;
              if (bidder) {
                return (
                  <RankingCard
                    key={idx}
                    bidder={bidder}
                    position={idx + 1}
                    startPrice={competitiveTension.startPrice}
                    leaderBid={leaderBid}
                  />
                );
              }
              return <EmptyRankingCard key={idx} position={idx + 1} />;
            })}

            {/* 5. STATS: Pujadores | Sin pujar */}
            <div className="bg-slate-700/30 rounded-xl border border-slate-600/50 p-4 flex items-center justify-around">
              <div className="text-center flex-1">
                <div className="text-3xl font-bold text-amber-400">{competitiveTension.totalBidders}</div>
                <div className="text-sm text-slate-400 mt-1">Pujadores</div>
              </div>
              <div className="w-px h-12 bg-slate-600" />
              <div className="text-center flex-1">
                <div className="text-3xl font-bold text-orange-400">{competitiveTension.attemptedWithoutBid}</div>
                <div className="text-sm text-slate-400 mt-1">Sin pujar</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* GRID: Top Activos + Fricción */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* TOP ACTIVOS SIN PUJAR */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-orange-400" />
            Top Activos sin Pujar
          </h3>

          {topActivosNoPujaron.length === 0 ? (
            <div className="text-center py-4 text-slate-500 text-sm">
              Sin usuarios activos pendientes de pujar
            </div>
          ) : (
            <div className="space-y-2">
              {topActivosNoPujaron.map((buyer, idx) => {
                const score = buyer.interestScore ?? buyer.score ?? 0;
                const hasAttempted = buyer.conversionStatus === 'attempted';
                return (
                  <div
                    key={buyer.email}
                    className="flex items-center justify-between bg-slate-700/30 rounded-lg px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-400">#{idx + 1}</span>
                      <span className="text-white text-sm">{buyer.name}</span>
                      {hasAttempted && (
                        <span className="text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">
                          intentó
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">{buyer.visits} visitas</span>
                      <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                        score >= 85 ? 'bg-green-500/20 text-green-400' :
                        score >= 60 ? 'bg-red-500/20 text-red-400' :
                        score >= 40 ? 'bg-amber-500/20 text-amber-400' :
                        score >= 20 ? 'bg-blue-500/20 text-blue-400' :
                        'bg-slate-600/20 text-slate-400'
                      }`}>
                        {score}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* FRICCIÓN */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            Fricción
          </h3>

          <div className="flex flex-wrap gap-4">
            <FrictionItem
              icon={UserX}
              label="Sin visitas"
              value={frictionMetrics.noVisits}
              color="red"
            />
            <FrictionItem
              icon={Eye}
              label="1 visita, no puja"
              value={frictionMetrics.singleVisit}
              color="orange"
            />
            <FrictionItem
              icon={Target}
              label="Intentaron, no puja"
              value={frictionMetrics.attemptedNotBid}
              color="amber"
            />
            <FrictionItem
              icon={XCircle}
              label="Abandonaron"
              value={frictionMetrics.abandoned}
              color="red"
            />
          </div>
        </div>
      </div>

      {/* PERFIL FINANCIERO */}
      {financialProfile && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-400" />
            Perfil Financiero
            <span className="text-slate-400 text-sm font-normal ml-2">
              ({financialProfile.total} con perfil)
            </span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Método de pago */}
            <FinancialBarChart
              title="Método de pago"
              icon={CreditCard}
              data={[
                { label: 'Contado', value: financialProfile.paymentMethod.cash, color: 'green' },
                { label: '<60%', value: financialProfile.paymentMethod.finance_under_60, color: 'blue' },
                { label: '60-80%', value: financialProfile.paymentMethod.finance_60_80, color: 'amber' },
                { label: '>80%', value: financialProfile.paymentMethod.finance_over_80, color: 'orange' },
                { label: 'Por decidir', value: financialProfile.paymentMethod.undecided, color: 'slate' },
              ]}
              total={financialProfile.total}
            />

            {/* Estado de financiación */}
            <FinancialBarChart
              title="Financiación"
              icon={Building}
              data={[
                { label: 'Preaprobada', value: financialProfile.financingStatus.preapproved, color: 'green' },
                { label: 'En proceso', value: financialProfile.financingStatus.in_process, color: 'blue' },
                { label: 'No iniciada', value: financialProfile.financingStatus.not_started, color: 'amber' },
                { label: 'No necesaria', value: financialProfile.financingStatus.no_need, color: 'slate' },
              ]}
              total={financialProfile.total}
            />

            {/* Propiedad que vender */}
            <FinancialBarChart
              title="Vivienda que vender"
              icon={Home}
              data={[
                { label: 'No', value: financialProfile.propertyToSell.no, color: 'green' },
                { label: 'Arras firmadas', value: financialProfile.propertyToSell.signed_deposit, color: 'blue' },
                { label: 'En venta', value: financialProfile.propertyToSell.on_sale, color: 'amber' },
                { label: 'Sin listar', value: financialProfile.propertyToSell.not_yet_listed, color: 'red' },
              ]}
              total={financialProfile.total}
            />
          </div>
        </div>
      )}

      {/* BOTÓN HEATMAP */}
      <div className="flex justify-center">
        <button
          onClick={() => setShowHeatmapModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-all border border-slate-600"
        >
          <Activity className="w-5 h-5 text-amber-400" />
          Ver Heatmap de Actividad
        </button>
      </div>

      {/* MODAL HEATMAP */}
      {showHeatmapModal && (
        <ActivityHeatmapModal
          propertyId={property?.id}
          buyerSummaries={buyerSummaries}
          onClose={() => setShowHeatmapModal(false)}
        />
      )}
    </div>
  );
};

// ============================================
// COMPONENTES AUXILIARES
// ============================================

const KpiCard = ({ icon: Icon, label, value, subtext, color = 'blue' }) => {
  const colors = {
    blue: 'from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-400',
    cyan: 'from-cyan-500/20 to-cyan-500/5 border-cyan-500/30 text-cyan-400',
    orange: 'from-orange-500/20 to-orange-500/5 border-orange-500/30 text-orange-400',
    green: 'from-green-500/20 to-green-500/5 border-green-500/30 text-green-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl border px-4 py-3 flex items-center justify-between`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 ${colors[color].split(' ').pop()}`} />
        <span className="text-sm text-slate-300">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-white">{value}</span>
        {subtext && <span className="text-sm text-slate-400">{subtext}</span>}
      </div>
    </div>
  );
};

const RankingCard = ({ bidder, position, startPrice, leaderBid }) => {
  const positionConfig = {
    1: { medal: '🥇', bg: 'bg-amber-500/10 border-amber-500/40', textColor: 'text-amber-400' },
    2: { medal: '🥈', bg: 'bg-slate-500/10 border-slate-500/40', textColor: 'text-slate-300' },
    3: { medal: '🥉', bg: 'bg-orange-700/10 border-orange-700/40', textColor: 'text-orange-400' },
  };

  const config = positionConfig[position];
  const percentVsStart = startPrice > 0 ? ((bidder.maxBid - startPrice) / startPrice * 100) : 0;
  const percentVsLeader = position > 1 && leaderBid > 0 ? ((bidder.maxBid - leaderBid) / leaderBid * 100) : 0;
  const isAboveStart = percentVsStart >= 0;

  const formatAmount = (amount) => {
    if (!amount) return '0€';
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(2)}M€`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}k€`;
    return `${amount.toLocaleString()}€`;
  };

  const displayName = bidder.name.length > 15 ? bidder.name.substring(0, 15) + '...' : bidder.name;

  return (
    <div className={`${config.bg} rounded-xl border p-4 flex flex-col justify-center text-center`}>
      {/* Medalla + Nombre centrados */}
      <div className="flex items-center justify-center gap-2 mb-1">
        <span className="text-2xl">{config.medal}</span>
        <span className="text-sm text-slate-300 truncate" title={bidder.name}>{displayName}</span>
      </div>

      {/* Monto centrado */}
      <div className={`text-2xl font-bold ${config.textColor} mb-1`}>
        {formatAmount(bidder.maxBid)}
      </div>

      {/* Pujas */}
      <div className="text-xs text-slate-500 mb-2">
        {bidder.bidsCount} puja{bidder.bidsCount !== 1 ? 's' : ''}
      </div>

      {/* Porcentajes */}
      <div className="flex items-center justify-center gap-3 text-xs">
        <span className={`flex items-center gap-1 ${isAboveStart ? 'text-green-400' : 'text-red-400'}`}>
          {isAboveStart ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {isAboveStart ? '+' : ''}{percentVsStart.toFixed(1)}%
        </span>
        {position > 1 && (
          <span className="text-red-400 flex items-center gap-1">
            <TrendingDown className="w-3 h-3" />
            {percentVsLeader.toFixed(1)}% vs 1º
          </span>
        )}
      </div>
    </div>
  );
};

const EmptyRankingCard = ({ position }) => {
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };

  return (
    <div className="bg-slate-700/20 rounded-xl border border-slate-600/30 border-dashed p-4 flex flex-col items-center justify-center">
      <span className="text-2xl opacity-30">{medals[position]}</span>
      <span className="text-sm text-slate-600 mt-1">Sin puja</span>
    </div>
  );
};

const FrictionItem = ({ icon: Icon, label, value, color = 'red' }) => {
  const colors = {
    red: 'text-red-400',
    orange: 'text-orange-400',
    amber: 'text-amber-400',
  };

  return (
    <div className="flex items-center gap-2 bg-slate-700/30 rounded-lg px-3 py-2">
      <Icon className={`w-4 h-4 ${colors[color]}`} />
      <span className="text-sm text-slate-300">{label}:</span>
      <span className={`font-bold ${colors[color]}`}>{value}</span>
    </div>
  );
};

const FinancialBarChart = ({ title, icon: Icon, data, total }) => {
  // Filtrar items con valor > 0
  const activeItems = data.filter(d => d.value > 0);

  const colors = {
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500',
    slate: 'bg-slate-500',
  };

  return (
    <div className="bg-slate-700/30 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-sm font-medium text-white">{title}</span>
      </div>

      {/* Barra segmentada */}
      <div className="h-3 bg-slate-600 rounded-full overflow-hidden flex mb-3">
        {data.map((item, idx) => {
          const percent = total > 0 ? (item.value / total) * 100 : 0;
          if (percent === 0) return null;
          return (
            <div
              key={idx}
              className={`${colors[item.color]} h-full`}
              style={{ width: `${percent}%` }}
              title={`${item.label}: ${item.value} (${percent.toFixed(0)}%)`}
            />
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {activeItems.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1 text-xs">
            <div className={`w-2 h-2 rounded-full ${colors[item.color]}`} />
            <span className="text-slate-400">{item.label}:</span>
            <span className="text-white font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardView;
